/**
 * @fileoverview Componente responsável por listar e atualizar o histórico de mensagens do ticket.
 */

import React, { useState, useEffect, useLayoutEffect, useReducer, useRef, useContext, useCallback, useMemo } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { isSameDay, parseISO, format, isToday, isYesterday } from "date-fns";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import { useTheme } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  makeStyles,
} from "@material-ui/core";

import { AccessTime, Done } from "@material-ui/icons";
import MarkdownWrapper from "../MarkdownWrapper";
import RotatableImage from "../RotatableImage";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ForwardMessageModal from "../ForwardMessageModal";
import MessageItem from "./MessageItem";
import { getDocumentInfo, DOC_EXT_COLORS } from "./documentMeta";
import whatsBackground from "../../assets/wa-background.png";
import LocationPreview from "../LocationPreview";

import whatsBackgroundDark from "../../assets/wa-background-dark.png"; //DARK MODE PLW DESIGN//

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { i18n } from "../../translate/i18n";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import { throttle } from "../../utils/performance";


import { ChevronDown, X, CheckCheck, Download, Send, Ban } from "lucide-react";
// Helper para formatar datas: "Hoje", "Ontem" ou data normal
const formatMessageDate = (dateString) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);

    // Verifica se a data é válida
    if (isNaN(date.getTime())) {
      return "";
    }

    if (isToday(date)) {
      return "Hoje";
    }

    if (isYesterday(date)) {
      return "Ontem";
    }

    return format(date, "dd/MM/yyyy");
  } catch (error) {
    console.error("Erro ao formatar data:", error);
    return "";
  }
};

// cor estável por nome: gera um hash e escolhe a cor de uma paleta fixa
const nameColorPalette = [
  "#e53935", // red
  "#8e24aa", // purple
  "#3949ab", // indigo
  "#1e88e5", // blue
  "#00acc1", // cyan
  "#00897b", // teal
  "#43a047", // green
  "#f4511e", // deep orange
  "#6d4c41", // brown
  "#546e7a"  // blue grey
];

const hashString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // to 32bit
  }
  return Math.abs(hash);
};

const getStableColor = (name = "") => {
  const hash = hashString(name.trim().toLowerCase());
  return nameColorPalette[hash % nameColorPalette.length];
};

// Helper para extrair o nome do remetente em mensagens de grupo
const getSenderName = (message, isGroup) => {
  if (!isGroup || message.fromMe) {
    return message.contact?.name;
  }

  // Para grupos, tentar extrair pushName do dataJson
  try {
    if (message.dataJson) {
      const data = JSON.parse(message.dataJson);
      // pushName está no nível raiz do dataJson
      if (data.pushName) {
        return data.pushName;
      }
    }
  } catch (e) {
    console.error("Erro ao extrair pushName:", e);
  }

  // Fallback para contact.name
  return message.contact?.name;
};

// helpers de mídia da mensagem citada
const isType = (t = "", name) => String(t || "").toLowerCase().includes(name);
const isImage = (t) => isType(t, "image");
const isAudio = (t) => isType(t, "audio") || isType(t, "ptt");
const isVideo = (t) => isType(t, "video");
const isDocument = (t) => isType(t, "application") || isType(t, "document") || isType(t, "pdf");

// helpers para detectar tipos de mídia por extensão ou URL
const isImageFile = (mediaUrl = "", body = "") => {
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
  return imageExtensions.test(mediaUrl) || imageExtensions.test(body);
};

const isVideoFile = (mediaUrl = "", body = "") => {
  const videoExtensions = /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i;
  return videoExtensions.test(mediaUrl) || videoExtensions.test(body);
};

const isAudioFile = (mediaUrl = "", body = "") => {
  const audioExtensions = /\.(mp3|wav|ogg|aac|m4a|opus)$/i;
  return audioExtensions.test(mediaUrl) || audioExtensions.test(body);
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
    maxHeight: "100%",
  },

  // Barra fina no topo da lista enquanto o "jump to quoted message" busca
  // a página correspondente no backend. Discreta pra não cobrir conteúdo mas
  // visível o bastante pra confirmar ao usuário que o clique foi registrado.
  jumpProgress: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    zIndex: 20,
  },
  
  forwardModeBar: {
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    borderTop: `1px solid ${theme.palette.primary.dark}`,
    zIndex: 1,
    height: 60,
  },
  
  forwardModeActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  
  messageSelected: {
    backgroundColor: `${theme.palette.primary.main}10`, // 10% transparência
    borderRadius: theme.shape.borderRadius,
  },
  
  
  

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`, //DARK MODE PLW DESIGN//
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: "#12A691",
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  scrollToBottomBtn: {
    position: "absolute",
    bottom: 16,
    right: 16,
    zIndex: 10,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    width: 40,
    height: 40,
    transition: "opacity 0.2s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
    },
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "#242626",
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "none",
  },

  messageLeftMedia: {
    maxWidth: 260,
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#f0f0f0" : "#2b2c2c",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    color: theme.palette.type === 'light' ? "#FFFFFF" : "#EAEAEA",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === 'light' ? "#6bcbef" : "#355e52",
  },

  // Card compacto pra documento dentro do preview de reply — mesma linguagem
  // visual do balão principal (badge colorido por extensão + nome + meta),
  // mas sem botão de download (clique no quote scrolla pra mensagem original).
  quotedDocCard: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 0",
    minWidth: 0,
  },
  quotedDocIcon: {
    width: 28,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  quotedDocInfo: {
    flex: 1,
    minWidth: 0,
  },
  quotedDocName: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#303030" : "#e9edef",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  quotedDocMeta: {
    fontSize: 10,
    color: theme.palette.type === 'light' ? "#667781" : "#8696a0",
    marginTop: 1,
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === 'light' ? "#DCF8C6" : "#274D37",
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "none",
  },

  messageRightMedia: {
    maxWidth: 260,
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#cfe9ba" : "#325543",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === 'light' ? "#35cd96" : "#3b6f5d",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "inherit",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  audioControl: {
    width: '100%',
    height: 36,
    backgroundColor: 'transparent',
    filter: theme.palette.type === 'light' ? 'none' : 'invert(0.85)',
    // Safari/Chrome audio UI sobre fundo escuro pode estourar contraste.
    // Para "colar" com o balão, ajustamos a cor do texto dos controles.
    color: theme.palette.type === 'light' ? '#303030' : '#EAEAEA',
  },
  audioWrapper: {
    display: 'block',
    width: '100%',
    minWidth: 220,
    maxWidth: 420,
    padding: '4px 6px 2px 6px',
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "auto",
    minWidth: "57px",
    backgroundColor: theme.mode === "light" ? "#fdfdfc" : "#222222",
    margin: "10px",
    borderRadius: "7px",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
    fontSize: "0.75rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: "#12A691",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    "& .MuiButton-label": {
      color: "#ffffff",
    },
  },

  reactionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
    marginBottom: 4,
    paddingLeft: 5,
    paddingRight: 5,
  },

  reactionBubble: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === 'light' ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)",
    borderRadius: 12,
    padding: "2px 8px",
    fontSize: 14,
    border: `1px solid ${theme.palette.type === 'light' ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)"}`,
  },

  reactionEmoji: {
    fontSize: 16,
    marginRight: 4,
  },

  reactionCount: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
  },
}));

// ✅ Helper para converter reactions de JSONB object para array
const convertReactionsToArray = (message) => {
  if (message.reactions && typeof message.reactions === 'object' && !Array.isArray(message.reactions)) {
    return {
      ...message,
      reactions: Object.keys(message.reactions).map(participant => ({
        text: message.reactions[participant].emoji,
        participant: participant,
        fromMe: message.reactions[participant].fromMe || false
      }))
    };
  }
  return message;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex === -1) {
        // ✅ Converte reactions ao carregar mensagens
        newMessages.push(convertReactionsToArray(message));
      }
    });

    // ✅ PROTEÇÃO: Ordena por createdAt após combinar (previne race conditions)
    // Sem isso, mensagens podem aparecer fora de ordem se chegarem via Socket.io
    // com latência variável de rede
    const combined = [...newMessages, ...state];
    return combined.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB; // Ordem cronológica: antigas → recentes
    });
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    let messageIndex = state.findIndex((m) => m.id === newMessage.id);

    // Se não encontrou pelo ID exato, verificar se já existe com sufixo _company_X
    if (messageIndex === -1) {
      messageIndex = state.findIndex((m) =>
        m.id.startsWith(newMessage.id + "_company_") ||
        newMessage.id.startsWith(m.id + "_company_")
      );
    }

    if (messageIndex !== -1) {
      // Mensagem já existe (possivelmente com sufixo), NÃO adicionar duplicata
      return state;
    }

    // ✅ PROTEÇÃO: Adiciona e ordena por createdAt (previne race condition)
    // Mensagens podem chegar fora de ordem via Socket.io devido latência
    const newState = [...state, newMessage];
    return newState.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB; // Ordem cronológica: antigas → recentes
    });
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    let messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    // Se não encontrou pelo ID original, tentar buscar pela versão com sufixo _company_X
    // Isso acontece quando mensagens são duplicadas por diferentes usuários
    if (messageIndex === -1) {
      // Tentar encontrar mensagem que comece com o mesmo ID base
      messageIndex = state.findIndex((m) =>
        m.id.startsWith(messageToUpdate.id + "_company_") ||
        messageToUpdate.id.startsWith(m.id + "_company_")
      );
    }

    if (messageIndex !== -1) {
      // ✅ Criar novo array com a mensagem atualizada
      const updatedState = [...state];
      updatedState[messageIndex] = {
        ...updatedState[messageIndex],
        body: messageToUpdate.body !== undefined ? messageToUpdate.body : updatedState[messageIndex].body,
        reactions: messageToUpdate.reactions || updatedState[messageIndex].reactions,
        ack: messageToUpdate.ack !== undefined ? messageToUpdate.ack : updatedState[messageIndex].ack,
        isDeleted: messageToUpdate.isDeleted !== undefined ? messageToUpdate.isDeleted : updatedState[messageIndex].isDeleted,
        isEdited: messageToUpdate.isEdited !== undefined ? messageToUpdate.isEdited : updatedState[messageIndex].isEdited,
        errorMessage: messageToUpdate.errorMessage !== undefined ? messageToUpdate.errorMessage : updatedState[messageIndex].errorMessage,
        // dataJson: usado por enquetes (votos em tempo real) e templates Meta
        dataJson: messageToUpdate.dataJson !== undefined ? messageToUpdate.dataJson : updatedState[messageIndex].dataJson,
        // Autoria da IA que chegou depois (o eco do envio foi gravado antes).
        aiSenderName: messageToUpdate.aiSenderName !== undefined ? messageToUpdate.aiSenderName : updatedState[messageIndex].aiSenderName,
      };
      return updatedState;
    }

    return state;
  }

  if (action.type === "ADD_OPTIMISTIC_MESSAGE") {
    const tempMsg = action.payload;
    const newState = [...state, tempMsg];
    return newState.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });
  }

  if (action.type === "RECONCILE_OPTIMISTIC") {
    const real = action.payload;
    const now = Date.now();

    // Procura a primeira temp pendente compatível (FIFO por findIndex).
    // Aceita _status "sending" OU "error" — em casos de falha do backend, o
    // .catch do api.post pode marcar como "error" antes do socket de
    // persistência da falha (local-<uuid>) chegar. Sem aceitar "error", a
    // reconciliação não acharia match e duplicaria a mensagem.
    const idx = state.findIndex(m =>
      m._optimistic &&
      (m._status === "sending" || m._status === "error") &&
      m.fromMe &&
      m.body === real.body &&
      (now - new Date(m.createdAt).getTime()) < 30000
    );

    if (idx === -1) {
      // Sem temp correspondente: comportamento normal do ADD_MESSAGE
      let messageIndex = state.findIndex((m) => m.id === real.id);
      if (messageIndex === -1) {
        messageIndex = state.findIndex((m) =>
          m.id.startsWith(real.id + "_company_") ||
          real.id.startsWith(m.id + "_company_")
        );
      }
      if (messageIndex !== -1) return state;

      const newState = [...state, real];
      return newState.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateA - dateB;
      });
    }

    // Substitui temp pela mensagem real in-place
    const next = [...state];
    next[idx] = { ...real };
    return next;
  }

  if (action.type === "MARK_OPTIMISTIC_ERROR") {
    const { tempId, errorMessage } = action.payload;
    return state.map(m =>
      m.id === tempId && m._optimistic && m._status === "sending"
        ? { ...m, _status: "error", ack: -1, errorMessage }
        : m
    );
  }

  // Remove mensagem por id — usado quando o backend deleta um local-<uuid>
  // após retry bem-sucedido (a nova com Meta ID já entra via ADD/RECONCILE).
  if (action.type === "REMOVE_MESSAGE") {
    const { id } = action.payload;
    return state.filter(m => m.id !== id);
  }

  if (action.type === "RESET") {
    return [];
  }

  // Substitui o estado pelas mensagens retornadas pelo endpoint /jump/:messageId.
  // O backend devolve da mensagem alvo até a mais recente (ordenado asc), então
  // basta converter reactions e setar como novo estado, descartando o que estava
  // carregado. O usuário pode rolar pra cima pra carregar mensagens anteriores
  // ao alvo via loadMore normal.
  if (action.type === "JUMP_TO_PAGE") {
    const messages = action.payload || [];
    return messages.map(convertReactionsToArray);
  }
};

// Helper para agrupar reações por emoji
const groupReactions = (reactions = []) => {
  if (!reactions || !Array.isArray(reactions)) return [];

  const grouped = {};
  reactions.forEach(reaction => {
    const emoji = reaction.text;
    if (!emoji) return;

    if (!grouped[emoji]) {
      grouped[emoji] = {
        emoji,
        count: 0,
        users: []
      };
    }
    grouped[emoji].count++;
    grouped[emoji].users.push({
      fromMe: reaction.fromMe,
      participant: reaction.participant
    });
  });

  return Object.values(grouped);
};

const MessagesList = ({ ticket, ticketId, isGroup, peek = false }) => {
  const classes = useStyles();
  const theme = useTheme();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // Força refetch da conversa mesmo quando pageNumber já é 1 — usado após
  // sync de histórico, onde novas msgs não vêm por appMessage (skipSocket).
  const [refetchToken, setRefetchToken] = useState(0);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  // True enquanto o jump-to-quoted está buscando a página no backend.
  // Mostra LinearProgress no topo da lista pra dar feedback de "clique recebido".
  const [jumping, setJumping] = useState(false);
  // Id da mensagem alvo do jump em andamento. Usado pelos efeitos de
  // scrollToBottom/fetchMessages pra se suprimirem enquanto o jump roda —
  // sem isso, mudar pageNumber pra 1 dispara o auto-scroll pro final e
  // o refetch da página 1 sobrescreve as mensagens do JUMP_TO_PAGE.
  const pendingJumpRef = useRef(null);
  const lastMessageRef = useRef();
  const scrollMetricsRef = useRef({ scrollTop: 0, scrollHeight: 0 });
  const scrollBtnTimerRef = useRef(null);
  const messagesListRef = useRef(null);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  // Estados para modo de encaminhamento
  const [forwardMode, setForwardMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [forwardModalOpen, setForwardModalOpen] = useState(false);

  const socketManager = useContext(SocketContext);
  const { setOptimisticDispatch } = useContext(ReplyMessageContext);

  // Expõe o dispatch para o MessageInputCustom conseguir criar mensagens optimistic
  useEffect(() => {
    setOptimisticDispatch(() => dispatch);
    return () => setOptimisticDispatch(null);
  }, [setOptimisticDispatch]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    // Durante um jump-to-quoted o estado já foi populado por JUMP_TO_PAGE.
    // Refazer GET /messages/:id?pageNumber=N aqui sobrescreveria o trecho
    // com a página padrão e destruiria o alvo. Sair antes de tudo (incluindo
    // setLoading) pra não disparar spinner.
    if (pendingJumpRef.current) return;
    setLoading(true);
    const fetchMessages = async () => {
      if (ticketId === undefined) return;
      try {
        const messagesDiv = messagesListRef.current;

        // ✅ ANTES de carregar: salvar scrollTop e scrollHeight
        if (messagesDiv && pageNumber > 1) {
          scrollMetricsRef.current = {
            scrollTop: messagesDiv.scrollTop,
            scrollHeight: messagesDiv.scrollHeight
          };
        }

        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber, peek: peek ? "true" : undefined },
        });

        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);

          // ✅ DEPOIS de carregar: calcular nova posição usando a fórmula correta
          // Fórmula: el.scrollTop = el.scrollHeight - prevScrollHeight + prevScrollTop
          if (pageNumber > 1 && messagesDiv) {
            setTimeout(() => {
              const newScrollHeight = messagesDiv.scrollHeight;
              const newScrollTop = newScrollHeight - scrollMetricsRef.current.scrollHeight + scrollMetricsRef.current.scrollTop;
              messagesDiv.scrollTop = newScrollTop;

              // ✅ IMPORTANTE: Setar loading=false DEPOIS de ajustar scroll
              setLoading(false);
            }, 100);
          } else {
            // Primeira página, libera loading imediatamente
            setLoading(false);
          }
        }

        // ✅ REMOVIDO: scrollToBottom() aqui causava race condition
        // Agora é chamado em useEffect separado após renderização completa
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };
    fetchMessages();
  }, [pageNumber, ticketId, refetchToken]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleReady = () => {
      socket.emit("joinChatBox", `${ticket.id}`);
    };

    const handleAppMessage = (data) => {
      if (data.action === "create" && data.message.ticketId === currentTicketId.current) {
        if (data.message.fromMe) {
          dispatch({ type: "RECONCILE_OPTIMISTIC", payload: data.message });
        } else {
          dispatch({ type: "ADD_MESSAGE", payload: data.message });
        }
      }

      if (data.action === "update" && data.message.ticketId === currentTicketId.current) {
        // ✅ Converte reactions de JSONB object para array
        const message = convertReactionsToArray(data.message);
        dispatch({ type: "UPDATE_MESSAGE", payload: message });
      }

      if (data.action === "delete" && data.message?.ticketId === currentTicketId.current) {
        dispatch({ type: "REMOVE_MESSAGE", payload: { id: data.message.id } });
      }
    };

    // Sync de histórico finalizou: força refetch da conversa atual pra trazer
    // as msgs importadas (skipSocket no backend não emite appMessage por msg).
    // Batch pra garantir 1 render só — React 17 não batcha em callbacks async
    // como handlers de socket listener.
    const handleSyncComplete = (data) => {
      if (Number(data?.ticketId) !== Number(currentTicketId.current)) return;
      unstable_batchedUpdates(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
        setRefetchToken((t) => t + 1);
      });
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-ticket-sync-complete`, handleSyncComplete);

    return () => {
      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(`company-${companyId}-ticket-sync-complete`, handleSyncComplete);
      socket.disconnect();
    };
  }, [ticketId, socketManager]); // ✅ Removido 'ticket' das dependências

  useEffect(() => {
    return () => {
      if (scrollBtnTimerRef.current) clearTimeout(scrollBtnTimerRef.current);
    };
  }, []);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }, [loading, hasMore]);

  const scrollToBottom = useCallback(() => {
    const messagesDiv = messagesListRef.current;
    if (messagesDiv) {
      messagesDiv.scrollTop = messagesDiv.scrollHeight;
    }
  }, []);

  // Scroll automático ao abrir conversa ou receber nova mensagem.
  // useLayoutEffect roda síncrono ANTES do paint — leitura de scrollHeight
  // força layout síncrono no momento certo, então o scrollTop é aplicado
  // antes do navegador renderizar o primeiro frame (sem flick visual).
  useLayoutEffect(() => {
    if (pendingJumpRef.current) return; // jump em andamento — não pular pro final
    if (pageNumber === 1 && messagesList.length > 0 && !loading) {
      scrollToBottom();
    }
  }, [messagesList.length, pageNumber, loading, scrollToBottom]);

  // Fallback iOS Safari: em containers flex que ainda se acomodam após o
  // commit, scrollHeight pode ter sido lido incorretamente. Re-aplica no
  // próximo frame como rede de segurança.
  useEffect(() => {
    if (pendingJumpRef.current) return;
    if (pageNumber === 1 && messagesList.length > 0 && !loading) {
      const id = requestAnimationFrame(scrollToBottom);
      return () => cancelAnimationFrame(id);
    }
  }, [messagesList.length, pageNumber, loading, scrollToBottom]);

  // ✅ Throttle no scroll para melhor performance
  const handleScroll = useMemo(
    () => throttle((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Mostrar botão scroll-to-bottom quando longe do final
      if (distanceFromBottom > 200) {
        if (scrollBtnTimerRef.current) clearTimeout(scrollBtnTimerRef.current);
        scrollBtnTimerRef.current = setTimeout(() => {
          setShowScrollBtn(true);
        }, 600);
      } else {
        if (scrollBtnTimerRef.current) clearTimeout(scrollBtnTimerRef.current);
        setShowScrollBtn(false);
      }

      if (!hasMore || loading) return;

      if (scrollTop === 0 && messagesListRef.current) {
        messagesListRef.current.scrollTop = 1;
      }

      // ✅ Threshold de 500 pixels para acionar paginação
      if (scrollTop < 500) {
        loadMore();
      }
    }, 150), // Executa no máximo a cada 150ms
    [hasMore, loading, loadMore]
  );

  const handleOpenMessageOptionsMenu = useCallback((e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  }, []);

  const handleCloseMessageOptionsMenu = useCallback((e) => {
    setAnchorEl(null);
  }, []);

  // Funções para modo de encaminhamento
  const handleEnableForwardMode = useCallback(() => {
    setForwardMode(true);
    setSelectedMessages([]);
  }, []);

  const handleDisableForwardMode = useCallback(() => {
    setForwardMode(false);
    setSelectedMessages([]);
  }, []);

  const handleMessageSelect = useCallback((messageId) => {
    setSelectedMessages(prev => {
      if (prev.includes(messageId)) {
        return prev.filter(id => id !== messageId);
      } else {
        return [...prev, messageId];
      }
    });
  }, []);

  const handleForwardMessages = useCallback(() => {
    const messagesToForward = messagesList.filter(msg => selectedMessages.includes(msg.id));
    setForwardModalOpen(true);
  }, [messagesList, selectedMessages]);

  const handleForwardComplete = useCallback(() => {
    setForwardModalOpen(false);
    setForwardMode(false);
    setSelectedMessages([]);
  }, []);

  const checkMessageMedia = (message) => {

    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    /* else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    } */
    /*else if (message.mediaType === "multi_vcard") {
      
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image" || isImageFile(message.mediaUrl, message.body)) {
      return <RotatableImage imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio" || isAudioFile(message.mediaUrl, message.body)) {
      return (
        <div className={classes.audioWrapper}>
          <audio controls className={classes.audioControl}>
            <source src={message.mediaUrl} type="audio/ogg"></source>
          </audio>
        </div>
      );
    } else if (message.mediaType === "video" || isVideoFile(message.mediaUrl, message.body)) {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<Download />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              {i18n.t("messagesList.header.buttons.download")}
            </Button>
          </div>
          <div style={{marginBottom: message.body === "" ? 8 : 0}}>
            <Divider />
          </div>
        </>
      );
    }
  };

  const getDataContextInfo = (data) => {
    if (!data) {
      return null;
    }

    return data.message?.extendedTextMessage?.contextInfo ||
      data.message?.imageMessage?.contextInfo ||
      data.message?.videoMessage?.contextInfo ||
      data.message?.audioMessage?.contextInfo ||
      data.message?.documentMessage?.contextInfo ||
      data.message?.stickerMessage?.contextInfo ||
      data.message?.productMessage?.contextInfo ||
      data.message?.locationMessage?.contextInfo ||
      data.message?.liveLocationMessage?.contextInfo ||
      data.message?.contactMessage?.contextInfo ||
      data.message?.listMessage?.contextInfo ||
      data.message?.buttonsResponseMessage?.contextInfo ||
      data.message?.paymentMessage?.contextInfo ||
      data.message?.orderMessage?.contextInfo ||
      data.message?.productCatalogMessage?.contextInfo ||
      data.message?.templateButtonReplyMessage?.contextInfo ||
      null;
  };

  const renderForwardedIndicator = (message, theme) => {
    // ✅ Melhorar detecção de mensagens encaminhadas usando método nativo do Baileys
    let isForwarded = message.isForwarded;
    let forwardingScore = 0;
    let dataContext = null;


    try {
      if (message.dataJson) {
        const parsedData = JSON.parse(message.dataJson);
        dataContext = parsedData.message?.extendedTextMessage?.contextInfo || 
                     parsedData.message?.imageMessage?.contextInfo ||
                     parsedData.message?.videoMessage?.contextInfo ||
                     parsedData.message?.audioMessage?.contextInfo ||
                     parsedData.message?.documentMessage?.contextInfo ||
                     parsedData.message?.stickerMessage?.contextInfo ||
                     parsedData.message?.contactMessage?.contextInfo ||
                     parsedData.message?.locationMessage?.contextInfo ||
                     parsedData.message?.buttonsResponseMessage?.contextInfo ||
                     parsedData.message?.listResponseMessage?.contextInfo ||
                     parsedData.message?.templateButtonReplyMessage?.contextInfo ||
                     null;

        if (dataContext) {
          // Verificar forwardingScore (número de encaminhamentos)
          if (dataContext.forwardingScore && dataContext.forwardingScore > 0) {
            isForwarded = true;
            forwardingScore = dataContext.forwardingScore;
          }
          
          // Verificar flag isForwarded
          if (dataContext.isForwarded === true) {
            isForwarded = true;
          }
        }

        // Verificar forwardingScore no nível da mensagem
        if (parsedData.message?.forwardingScore && parsedData.message.forwardingScore > 0) {
          isForwarded = true;
          forwardingScore = parsedData.message.forwardingScore;
        }
      }
    } catch (error) {
      console.error("Erro ao analisar dataJson da mensagem:", error);
    }

    if (isForwarded) {
      return (
        <div style={{
          display: "flex",
          alignItems: "center",
          fontSize: "11px",
          color: theme.palette.mode === 'light' ? "#4a4a4a" : "#8696a0",
          marginBottom: "4px",
          marginTop: "2px",
          fontWeight: "400"
        }}>
          {/* Ícone de encaminhamento igual ao WhatsApp */}
          <span style={{ 
            fontSize: "14px", 
            marginRight: "4px",
            color: theme.palette.mode === 'light' ? "#4a4a4a" : "#8696a0",
            display: "inline-flex",
            alignItems: "center"
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1.01 7L5 3c1.1 0 2 .9 2 2v2h8c1.1 0 2 .9 2 2v5l-2-2V9H7v2l-4-3.99L1.01 7zM15 15v2l4-3.99L15 9v2H7V7H5v2l-4-3.99L1.01 7L5 3c1.1 0 2 .9 2 2v2h8c1.1 0 2 .9 2 2v5l-2-2V9H7v2l-4-3.99L1.01 7z"/>
            </svg>
          </span>
          Encaminhada
          {forwardingScore > 1 && (
            <span style={{ 
              marginLeft: "4px",
              fontSize: "10px",
              opacity: 0.8
            }}>
              ({forwardingScore}x)
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0 || message.ack === undefined || message.ack === null) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <CheckCheck size={18} className={classes.ackIcons} />;
    }
    if (message.ack >= 3) {
      return <CheckCheck size={18} className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div className={classes.dailyTimestampText}>
            {formatMessageDate(messagesList[index].createdAt)}
          </div>
        </span>
      );
    }

    try {
      if (!messagesList[index].createdAt || !messagesList[index - 1].createdAt) {
        return null;
      }

      const messageDay = parseISO(messagesList[index].createdAt);
      const previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (isNaN(messageDay.getTime()) || isNaN(previousMessageDay.getTime())) {
        return null;
      }

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>
              {formatMessageDate(messagesList[index].createdAt)}
            </div>
          </span>
        );
      }
    } catch (error) {
      console.error("Erro ao renderizar timestamps:", error);
      return null;
    }

    return null;
  };

  const renderNumberTicket = (message, index) => {
    if (index < messagesList.length && index > 0) {

      let messageTicket = message.ticketId;
      let connectionName = message.ticket?.whatsapp?.name;
      let previousMessageTicket = messagesList[index - 1].ticketId;

      if (messageTicket !== previousMessageTicket) {
        try {
          const prevDate = messagesList[index - 1].createdAt ? parseISO(messagesList[index - 1].createdAt) : null;
          const currDate = message.createdAt ? parseISO(message.createdAt) : null;

          // Verifica se as datas são válidas
          const prevDateStr = prevDate && !isNaN(prevDate.getTime())
            ? format(prevDate, "dd/MM/yyyy HH:mm:ss")
            : "Data indisponível";
          const currDateStr = currDate && !isNaN(currDate.getTime())
            ? format(currDate, "dd/MM/yyyy HH:mm:ss")
            : "Data indisponível";

          return (
            <center>
              <div className={classes.ticketNunberClosed}>
                Conversa encerrada: {prevDateStr}
              </div>

              <div className={classes.ticketNunberOpen}>
                Conversa iniciada: {currDateStr}
              </div>
            </center>
          );
        } catch (error) {
          console.error("Erro ao renderizar mudança de ticket:", error);
          return null;
        }
      }
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      // Espaço quando muda quem fala: cliente e empresa e, do lado da empresa,
      // a IA e o atendente (ou outra IA).
      const speakerOf = (m) => (m.fromMe ? `empresa:${m.aiSenderName || ""}` : "cliente");
      let messageUser = speakerOf(messagesList[index]);
      let previousMessageUser = speakerOf(messagesList[index - 1]);

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const getBubbleColors = (fromMe) => {
    if (fromMe) {
      return {
        bg: (typeof window !== 'undefined' && window.getComputedStyle ? '' : ''),
        bgResolved: (document && document.body ? null : null),
      };
    }
    return { bg: '', bgResolved: null };
  };

  const getExcerpt = (text = "", max = 120) => {
    const normalized = String(text).replace(/\s+/g, " ").trim();
    if (normalized.length <= max) return normalized;
    return normalized.slice(0, max - 1) + "…";
  };

  const renderReactions = (message) => {
    if (!message.reactions || !Array.isArray(message.reactions) || message.reactions.length === 0) {
      return null;
    }

    const groupedReactions = groupReactions(message.reactions);

    return (
      <div className={classes.reactionsContainer}>
        {groupedReactions.map((reaction, index) => (
          <div key={`${message.id}-reaction-${index}`} className={classes.reactionBubble}>
            <span className={classes.reactionEmoji}>{reaction.emoji}</span>
            {reaction.count > 1 && (
              <span className={classes.reactionCount}>{reaction.count}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Aplica o efeito visual de "cheguei" numa mensagem alvo: scroll (smooth se
  // já está perto, instant se está longe — evita scroll de 5 telas) e classe
  // .message-highlight que dispara o pulso duplo definido em App.js.
  const highlightAndScroll = useCallback((el) => {
    const container = messagesListRef.current;
    if (!el || !container) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const distance = Math.abs(
      (elRect.top + elRect.height / 2) - (containerRect.top + containerRect.height / 2)
    );
    const behavior = distance > container.clientHeight * 1.5 ? "auto" : "smooth";

    try {
      el.scrollIntoView({ behavior, block: "center" });
      el.classList.add("message-highlight");
      setTimeout(() => {
        if (el && document.contains(el)) {
          el.classList.remove("message-highlight");
        }
      }, 1600);
    } catch (error) {
      console.debug("highlightAndScroll failed:", error);
    }
  }, []);

  const scrollToQuoted = useCallback(async (quotedId) => {
    if (!quotedId) return;

    // Caso 1: mensagem já está renderizada — scroll direto.
    const existing = document.getElementById(`message-${quotedId}`);
    if (existing && document.contains(existing)) {
      highlightAndScroll(existing);
      return;
    }

    // Caso 2: mensagem está fora da janela carregada. Busca a página no
    // backend e substitui o estado pelo trecho (alvo → mais recente). Mostra
    // LinearProgress no topo enquanto busca.
    if (jumping) return; // evita cliques múltiplos durante o fetch
    setJumping(true);
    // pendingJumpRef ativo suprime: (a) refetch automático em [pageNumber]
    // (b) scrollToBottom de useLayoutEffect/useEffect. Sem isso, o setPageNumber
    // abaixo dispara esses 3 efeitos antes do nosso scroll, e a página alvo
    // some — exatamente o "vai e volta" reportado.
    pendingJumpRef.current = quotedId;
    try {
      const { data } = await api.get(`/messages/${ticketId}/jump/${quotedId}`);
      if (currentTicketId.current !== ticketId) {
        pendingJumpRef.current = null;
        return;
      }

      unstable_batchedUpdates(() => {
        dispatch({ type: "JUMP_TO_PAGE", payload: data.messages });
        setPageNumber(data.pageNumber || 1);
        setHasMore(!!data.hasMore);
        setLoading(false);
      });

      // Aguarda o React commit + paint pra garantir que o id da mensagem alvo
      // está no DOM antes de tentar scroll. requestAnimationFrame duplo é o
      // padrão pra "após o próximo paint" — só então liberamos o ref.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById(`message-${quotedId}`);
          if (el && document.contains(el)) {
            highlightAndScroll(el);
          }
          pendingJumpRef.current = null;
        });
      });
    } catch (err) {
      pendingJumpRef.current = null;
      // 404 = mensagem deletada ou inacessível. Outros = erro real.
      if (err?.response?.status === 404) {
        toastError({
          response: { data: { message: "Mensagem original não encontrada" } }
        });
      } else {
        toastError(err);
      }
    } finally {
      setJumping(false);
    }
  }, [ticketId, jumping, highlightAndScroll]);

  const renderQuotedMessage = (message) => {
    const quotedFromMe = !!message.quotedMsg?.fromMe;
    const bubbleBg = quotedFromMe
      ? ( (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? '#274D37' : '#DCF8C6')
      : ( (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? '#242626' : '#FFFFFF');

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        style={{ backgroundColor: bubbleBg, cursor: message.quotedMsg?.id ? 'pointer' : 'default' }}
        onClick={() => message.quotedMsg?.id && scrollToQuoted(message.quotedMsg.id)}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {isAudio(message.quotedMsg.mediaType) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4, paddingBottom: 2 }}>
              <AudiotrackIcon style={{ fontSize: 18, opacity: 0.9 }} />
              <span>{i18n.t("messagesList.audio") || "Áudio"}</span>
            </div>
          )}
          {isVideo(message.quotedMsg.mediaType)
            && (
              <video
                className={classes.messageMedia}
                src={message.quotedMsg.mediaUrl}
                controls
              />
            )
          }
          {isDocument(message.quotedMsg.mediaType) && (() => {
            const { fileName, fileSize, ext } = getDocumentInfo(message.quotedMsg);
            const extColor = DOC_EXT_COLORS[ext] || "#667781";
            return (
              <div className={classes.quotedDocCard}>
                <div className={classes.quotedDocIcon} style={{ backgroundColor: extColor }}>
                  {ext || "DOC"}
                </div>
                <div className={classes.quotedDocInfo}>
                  <div className={classes.quotedDocName} title={fileName}>
                    {fileName}
                  </div>
                  <div className={classes.quotedDocMeta}>
                    {[ext, fileSize].filter(Boolean).join(" • ")}
                  </div>
                </div>
              </div>
            );
          })()}

          {isImage(message.quotedMsg.mediaType) && (
            <div style={{ borderRadius: 6, overflow: 'hidden' }}>
              <RotatableImage imageUrl={message.quotedMsg.mediaUrl} />
            </div>
          )}

          {message.quotedMsg.mediaType === "contactMessage"
            && (
                <span>{message.quotedMsg.body}</span>
              )
          }

          {/* Conteúdo textual - sempre mostra se houver body e não for mídia específica */}
          {message.quotedMsg?.body &&
           !isImage(message.quotedMsg.mediaType) &&
           !isAudio(message.quotedMsg.mediaType) &&
           !isVideo(message.quotedMsg.mediaType) &&
           !isDocument(message.quotedMsg.mediaType) &&
           message.quotedMsg.mediaType !== "contactMessage" && (
            <span>{getExcerpt(message.quotedMsg.body)}</span>
          )}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {

        // Notificações de sistema (entrada/saída/promote/demote em grupos).
        // Renderiza como bolha centralizada cinza (padrão WhatsApp).
        if (message.mediaType === "notification") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "8px 0",
                  width: "100%"
                }}
              >
                <span
                  style={{
                    background: "rgba(142, 142, 147, 0.18)",
                    color: "#8e8e93",
                    padding: "4px 12px",
                    borderRadius: "10px",
                    fontSize: "12.5px",
                    maxWidth: "80%",
                    textAlign: "center",
                    lineHeight: 1.3
                  }}
                >
                  {message.body}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ChevronDown />
                </IconButton>
                {isGroup && !message.fromMe && (
                  <span className={classes.messageContactName} style={{ color: getStableColor(getSenderName(message, isGroup)) }}>
                    {getSenderName(message, isGroup)}
                  </span>
                )}
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>{i18n.t("messagesList.lostCall")} {(() => {
                    try {
                      const date = message.createdAt ? parseISO(message.createdAt) : null;
                      return date && !isNaN(date.getTime()) ? format(date, "HH:mm") : "";
                    } catch (e) {
                      return "";
                    }
                  })()}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        // ✅ Usar componente otimizado MessageItem
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderNumberTicket(message, index)}
            {renderMessageDivider(message, index)}
            <MessageItem
              message={message}
              isGroup={isGroup}
              forwardMode={forwardMode}
              selectedMessages={selectedMessages}
              onOpenMessageOptionsMenu={handleOpenMessageOptionsMenu}
              onMessageSelect={handleMessageSelect}
              onQuotedClick={scrollToQuoted}
            />
          </React.Fragment>
        );
      });
      return viewMessagesList;
    } else {
      return <div>{i18n.t("messagesList.saudation")}</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      {jumping && (
        <LinearProgress className={classes.jumpProgress} color="primary" />
      )}
      <MessageOptionsMenu
        message={selectedMessage}
        ticket={ticket}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        onEnableForwardMode={handleEnableForwardMode}
      />
      <div
        ref={messagesListRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}

      {showScrollBtn && (
        <IconButton
          className={classes.scrollToBottomBtn}
          onClick={() => {
            setShowScrollBtn(false);
            scrollToBottom();
          }}
          size="small"
        >
          <ChevronDown />
        </IconButton>
      )}
      
      {forwardMode && (
        <div className={classes.forwardModeBar}>
          <span>
            ✕ {selectedMessages.length} mensagem(ns) selecionada(s)
          </span>
          <div className={classes.forwardModeActions}>
            <IconButton
              onClick={handleDisableForwardMode}
              style={{ color: 'white' }}
            >
              <X />
            </IconButton>
            <IconButton
              onClick={handleForwardMessages}
              disabled={selectedMessages.length === 0}
              style={{ color: 'white' }}
            >
              <Send />
            </IconButton>
          </div>
        </div>
      )}
      
      {/* Modal de encaminhamento */}
      <ForwardMessageModal
        open={forwardModalOpen}
        onClose={handleForwardComplete}
        messages={messagesList.filter(msg => selectedMessages.includes(msg.id))}
      />
    </div>
  );
};

export default MessagesList;
